import { useLingui } from '@lingui/react/macro';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { CurrencyConversionRateWithDate, Endpoints } from '@/api';
import { RequestWithRelations } from '@/models/request';
import { useFetch } from '@/utils/fetch';
import { Currency } from '@/utils/number';

export type TransactionConversion = {
  currency: Currency;
  amount: number;
  rate: CurrencyConversionRateWithDate;
};

export const useTransactionOriginName = (transaction: {
  type: TransactionFromGet['type'];
  originClient: null | { name: string | null };
  onBehalfOfCompany: null | { name: string };
  originCompany: null | { name: string };
}) => {
  const { t } = useLingui();

  if (transaction.type === 'client_payment_in' && transaction.originClient) {
    return (
      transaction.originClient.name || t({ id: 'transaction.origin-client', message: 'Client' })
    );
  }

  if (transaction.type === 'workshop_payment_out' && transaction.onBehalfOfCompany) {
    return transaction.onBehalfOfCompany.name;
  }

  if (transaction.type === 'client_refund' && transaction.originCompany) {
    return transaction.originCompany.name;
  }

  return '-';
};

export const useTransactionDestinationName = (transaction: {
  type: TransactionFromGet['type'];
  destinationCompany: null | { name: string };
  onBehalfOfCompany: null | { name: string };
  destinationClient: null | { name: string | null };
}) => {
  const { t } = useLingui();

  if (transaction.type === 'workshop_payment_out' && transaction.destinationCompany) {
    return transaction.destinationCompany.name;
  }

  if (transaction.type === 'client_payment_in' && transaction.onBehalfOfCompany) {
    return transaction.onBehalfOfCompany.name;
  }

  if (transaction.type === 'client_payment_in' && transaction.destinationCompany) {
    return transaction.destinationCompany.name;
  }

  if (transaction.type === 'client_refund' && transaction.destinationClient) {
    return (
      transaction.destinationClient.name ||
      t({ id: 'transaction.destination-client', message: 'Client' })
    );
  }

  return '-';
};

export type TransactionFromGetMany =
  Endpoints['GET /transactions']['response']['transactions'][number];

export type TransactionFromGetClientPaymentTransactions =
  Endpoints['GET /requests/:id/client-payment-transactions']['response'][number];

export const useTransactions = (params?: Endpoints['GET /transactions']['query']) => {
  const fetch = useFetch<Endpoints['GET /transactions']>();

  return useQuery({
    queryKey: ['transactions', params],
    queryFn: () => fetch('/transactions', params),
  });
};

export const useClientPaymentTransactions = (requestId: string) => {
  const fetch = useFetch<Endpoints['GET /requests/:id/client-payment-transactions']>();

  return useQuery({
    queryKey: ['client-payment-transactions', requestId],
    queryFn: () => fetch(`/requests/${requestId}/client-payment-transactions`),
  });
};

export type TransactionFromGet = Endpoints['GET /transactions/:id']['response'];
export const useTransaction = (
  transactionId?: string | null,
  {
    enabled,
    refetchIntervalWhilePending,
  }: { enabled?: boolean; refetchIntervalWhilePending?: number } = {}
) => {
  const fetch = useFetch<Endpoints['GET /transactions/:id']>();

  return useQuery({
    queryKey: ['transactions', transactionId],
    queryFn: () => fetch(`/transactions/${transactionId}`),
    enabled: (enabled ?? true) && !!transactionId,
    refetchInterval: (query) =>
      query.state.data?.status === 'pending' ? refetchIntervalWhilePending : undefined,
  });
};

export const useCancelTransaction = () => {
  const fetch = useFetch<Endpoints['POST /transactions/:id/cancel']>();

  return useMutation({
    mutationFn: (transactionId: string) =>
      fetch(`/transactions/${transactionId}/cancel`, undefined, { method: 'POST' }),
  });
};

export const useValidateTransaction = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /transactions/:id/validate']>();

  return useMutation({
    mutationFn: ({
      transactionId,
      paymentReference,
    }: {
      transactionId: string;
      paymentReference?: string;
    }) =>
      fetch(`/transactions/${transactionId}/validate`, undefined, {
        method: 'POST',
        body: { paymentReference },
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.invalidateQueries({ queryKey: ['client-payment-transactions'] });
    },
  });
};

export const isClientRefundTransaction = (
  transaction: Pick<TransactionFromGetClientPaymentTransactions, 'type'>
) => transaction.type === 'client_refund';

export const partitionClientPaymentTransactions = (
  transactions: TransactionFromGetClientPaymentTransactions[]
) => ({
  payments: transactions.filter((transaction) => !isClientRefundTransaction(transaction)),
  refunds: transactions.filter((transaction) => isClientRefundTransaction(transaction)),
});

export type AmountWithConversions = {
  amount: number;
  currency: Currency;
  conversions: TransactionConversion[];
};

export const getRequestAmountPaid = (
  transactions: TransactionFromGetClientPaymentTransactions[]
): AmountWithConversions | null => {
  if (!transactions.length) {
    return null;
  }

  const amount = transactions.reduce(
    (total, transaction) =>
      total + (isClientRefundTransaction(transaction) ? -1 : 1) * transaction.amount,
    0
  );

  return {
    amount,
    currency: transactions[0].currency,
    conversions: transactions.reduce((acc, transaction) => {
      const sign = isClientRefundTransaction(transaction) ? -1 : 1;

      transaction.conversions.forEach((conversion) => {
        const existing = acc.find((item) => item.currency === conversion.currency);

        if (existing) {
          existing.amount += conversion.amount * sign;
        } else {
          acc.push({
            currency: conversion.currency,
            amount: conversion.amount * sign,
            rate: conversion.rate,
          });
        }
      });

      return acc;
    }, [] as TransactionConversion[]),
  };
};

export const getRequestBalanceDue = (
  request: Pick<RequestWithRelations, 'organizationPrice'>,
  amountPaid: AmountWithConversions | null
): {
  balance: AmountWithConversions | null;
  isRefund: boolean;
} => {
  const organizationPrice = request.organizationPrice?.amountPerCurrency?.[0];

  if (!organizationPrice) {
    return { balance: null, isRefund: false };
  }

  const balanceAmount = organizationPrice.amount - (amountPaid?.amount ?? 0);

  const conversions: {
    amount: number;
    currency: Currency;
    rate: CurrencyConversionRateWithDate;
  }[] = Object.values(organizationPrice.conversions).map((conversion) => {
    const amountPaidConversion = amountPaid?.conversions?.find(
      (c) => c.currency === conversion.amount.currency
    );

    if (!amountPaidConversion) {
      return {
        amount: conversion.amount.amount,
        currency: conversion.amount.currency,
        rate: conversion.rate,
      };
    }

    return {
      amount: conversion.amount.amount - (amountPaidConversion.amount ?? 0),
      currency: conversion.amount.currency,
      rate: conversion.rate,
    };
  });

  return {
    balance: {
      amount: balanceAmount,
      currency: organizationPrice.currency,
      conversions,
    },
    isRefund: balanceAmount < 0,
  };
};

export const shouldShowClientPaymentSection = ({
  canSeeClientPrice,
  transactionsCount,
  request,
}: {
  canSeeClientPrice: boolean;
  transactionsCount: number;
  request: RequestWithRelations;
}) => {
  if (!canSeeClientPrice) {
    return false;
  }

  const hasPaymentOrRefundStepInArticles = request.articles.some((article) =>
    (article.steps ?? []).some((step) => step.step === 'payment' || step.step === 'refund')
  );
  const hasClientPriceDefined = !!request.organizationPrice?.amountPerCurrency?.[0];

  return transactionsCount > 0 || (hasPaymentOrRefundStepInArticles && hasClientPriceDefined);
};
